import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Info } from 'lucide-react';
import Modal from 'components/ui/Modal';
import { Button } from 'components/ui/button';
import { useImportAiRepairStatus, useImportPreview } from 'modules/questionBank';
import { useImportReviewStore } from 'store/importReview.store';
import { ImportAiToggle } from './ImportAiToggle';
import { ImportDropzone } from './ImportDropzone';
import { ImportProcessingPanel } from './ImportProcessingPanel';
import { ImportTemplateCard } from './ImportTemplateCard';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  defaultSubjectId?: string;
}

const ACCEPTED_EXTENSIONS = '.xlsx,.csv,.json,.txt,.gift,.pdf,.docx';

/**
 * Upload step of the file import.
 *
 * Once the file parses, review happens on its own page (`ImportReviewPage`) —
 * the same editable cards as an AI-generated batch, where the destination
 * subject and the correct answers are chosen. Nothing here writes; nothing
 * there writes until the batch is committed.
 */
export const ImportQuestionsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  defaultSubjectId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const startReview = useImportReviewStore((state) => state.start);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selected, setSelected] = useState<{ name: string; size: number } | null>(null);
  // Off on every opening: the AI pass is a per-upload choice, never remembered.
  const [aiRepair, setAiRepair] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const previewImport = useImportPreview();
  const aiStatus = useImportAiRepairStatus(isOpen);
  const aiAvailable = aiStatus.data?.enabled === true;
  const aiOn = aiAvailable && aiRepair;

  const reset = () => {
    // A read still in flight (the AI pass can take a while) is cancelled, not left to land on a closed dialog.
    abortRef.current?.abort();
    abortRef.current = null;
    setSelected(null);
    setAiRepair(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Closing from anywhere — the parent, Escape, the backdrop — must not leave a
  // parsed file behind for the next time the modal opens.
  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    // `mutate` (not `mutateAsync`) so React Query owns the failure path: the
    // hook toasts, and the HTTP layer raises the upgrade dialog on entitlement
    // 403s. An awaited `mutateAsync` would reject unhandled here.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setSelected({ name: file.name, size: file.size });
    previewImport.mutate(
      // The subject is a review-time decision now; passing the current filter
      // only pre-resolves rows whose own `Subject` column names nothing.
      { file, subjectId: defaultSubjectId, aiRepair: aiOn, signal: controller.signal },
      {
        onSuccess: (result) => {
          if (!result) return;
          startReview(result, defaultSubjectId);
          handleClose();
          navigate('/app/questions/import/review');
        },
        // Clear the input either way, so re-picking the same file after a
        // failed read still fires `change`.
        onSettled: () => {
          if (fileInputRef.current) fileInputRef.current.value = '';
        },
      },
    );
  };

  const reading = previewImport.isPending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('import_questions')}
      description={t('import_questions_desc')}
      size="xl"
    >
      <div className="space-y-5">
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          className="hidden"
          onChange={(event) => void handleFile(event.target.files?.[0] ?? undefined)}
        />

        {reading ? (
          <ImportProcessingPanel fileName={selected?.name ?? ''} fileSize={selected?.size ?? 0} aiOn={aiOn} />
        ) : (
          <>
            <div className="space-y-2">
              <ImportDropzone onBrowse={() => fileInputRef.current?.click()} onFile={handleFile} />
              <p className="flex items-start gap-2 px-1 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{t('import_prose_hint')}</span>
              </p>
            </div>

            <div className={aiAvailable ? 'grid gap-3 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]' : undefined}>
              {aiAvailable && <ImportAiToggle checked={aiRepair} onChange={setAiRepair} />}
              <ImportTemplateCard />
            </div>
          </>
        )}

        <div className="flex items-center justify-between gap-4 border-t pt-4">
          <p className="min-w-0 text-xs text-muted-foreground">
            {reading ? t('import_working_hint') : t('import_auto_start_hint')}
          </p>
          <Button variant="outline" onClick={handleClose} className="shrink-0">
            {t('cancel')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ImportQuestionsModal;
